import { defineStore } from 'pinia';
import ListsUpdateService from '../services/lists-update';
import ListsCreateService from '../services/lists-create';

export const useWishlistStore = defineStore('wishlist', {
  state: () => ({
    errorKey: null,
    serviceError: false,
    wishlist: [],
    availableChanges: false,
    paginator: {
      counter: 0,
      itemsPerPage: 15,
      pageCounter: 0,
      pageSelected: 0,
    },
    addToCartState: {
      backOrderUrl: '/backOrderController?sku={sku}&quantity={quantity}&storeId=8000',
      cartData: {},
      acceptTermsConditions: false,
      modalIsOpen: false,
      modal: {
        label: '',
        modalContent: 'ErrorModal',
        labelIsTitle: false,
        // modalProps can be a string or an object
        modalProps: {
          message: 'default error message',
        },
      },
    },
    cart: {},
    cartLoader: false,
    itemRemovedMoved: {},
  }),
  getters: {
    wishlistEntries(state) {
      return state.wishlist.entries ? state.wishlist.entries : [];
    },
    selectedItem(state) {
      return state.addToCartState.cartData
        ? {
            id: state.addToCartState.cartData.item,
            sku: state.addToCartState.cartData.sku,
            quantity: state.addToCartState.cartData.quantity,
            price: state.addToCartState.cartData.price,
            description: state.addToCartState.cartData.description,
          }
        : {};
    },
    acceptanceTermsState(state) {
      return state.addToCartState.acceptTermsConditions;
    },
    cartErrorData(state) {
      const { addToCartState } = state;
      const termsErr = 10213;
      const lineItemCountErr = 10219;
      const code = Array.isArray(addToCartState.cartData.cartItemIssueDtoList)
        ? addToCartState.cartData.cartItemIssueDtoList[0].code
        : undefined;
      const title =
        code === lineItemCountErr
          ? "You've reached the maximum number of items allowed in your cart."
          : 'Oops, something went wrong!';
      let scenario;
      if (code === termsErr) scenario = 'terms';
      if (code === lineItemCountErr) scenario = 'limit';
      return {
        title,
        scenario,
        hasDefinedError: scenario !== undefined,
      };
    },
    wishlistPageEntries(state) {
      return state.wishlist && state.wishlist.entries
        ? state.wishlist.entries.slice(state.paginator.counter, state.paginator.counter + state.paginator.itemsPerPage)
        : [];
    },
    paginatorNumbers(state) {
      return {
        pages: [...Array(Math.ceil(state.wishlist.entries.length / state.paginator.itemsPerPage)).keys()],
        pageSelected: state.paginator.pageSelected,
        itemsPerPage: state.paginator.itemsPerPage,
        counter: state.paginator.counter,
      };
    },
  },
  actions: {
    redirect(url) {
      return window.location.assign(url);
    },
    previousPage() {
      this.paginator.counter -= this.paginator.itemsPerPage;
      this.paginator.pageSelected -= 1;
    },
    recalculate() {
      if (!this.paginatorNumbers.pages.includes(this.paginatorNumbers.pageSelected)) {
        this.previousPage();
      }
    },
    async updateList(listData) {
      return ListsUpdateService.putList(listData)
        .then((response) => {
          if (!response.ok) {
            let data = response;
            try {
              data = response.json();
            } catch (error) {
              this.errorKey = error;
            }
            return Promise.resolve(data);
          }
          return Promise.resolve({});
        })
        .then((data) => {
          const { error } = data;
          if (error) {
            this.errorKey = error;
          }
          return Promise.resolve(error);
        })
        .catch((error) => {
          console.error('Error:', error);
        });
    },
    async deleteList(listId) {
      return ListsUpdateService.deleteList(listId)
        .then((response) => {
          if (response.ok) {
            this.redirect('/lists');
          } else {
            throw Error(response.error);
          }
        })
        .catch((error) => {
          console.error('Error:', error);
        });
    },
    async deleteItemFromBack(listData) {
      return ListsUpdateService.deleteItem(listData)
        .then((response) => {
          if (!response.ok) {
            let data = response;
            try {
              data = response.json();
            } catch (error) {
              this.errorKey = error;
            }
            return Promise.resolve(data);
          }
          this.recalculate();
          return Promise.resolve({});
        })
        .then((data) => {
          const { error } = data;
          if (error) {
            this.errorKey = error;
          }
        })
        .catch((error) => error);
    },
    deleteItemFromLocalStorage(listData) {
      const indexItem = this.wishlistEntries.findIndex((x) => x.id === listData.item);
      this.itemRemovedMoved = {};
      this.wishlist.entries.splice(indexItem, 1);
    },
    async updateItem(listData) {
      return ListsUpdateService.updateItem(listData)
        .then((response) => {
          if (response.ok) {
            const index = this.wishlistEntries.findIndex((x) => x.id === listData.item);
            const { quantity } = listData;
            this.wishlist.entries[index].quantity = parseInt(quantity, 10);
            return Promise.resolve({});
          }
          return Promise.resolve(response.json());
        })
        .then((data) => {
          const { error } = data;
          if (error) {
            this.errorKey = error;
          }
        })
        .catch((error) => error);
    },
    async addComment(listData) {
      return ListsUpdateService.addComment(listData)
        .then((response) => {
          if (!response.ok) {
            return Promise.resolve(response.json());
          }
          const index = this.wishlistEntries.findIndex((x) => x.id === listData.item);
          const { note } = listData;
          this.wishlist.entries[index].comment = note;

          return Promise.resolve({});
        })
        .then((data) => {
          const { error } = data;
          if (error) {
            this.errorKey = error;
          }
        })
        .catch((error) => error);
    },
    async moveItemFromBack(listData) {
      let data = {};
      try {
        const response = await ListsUpdateService.updateItem(listData);
        if (response.ok) {
          this.recalculate();
        } else {
          data = response;
          data = response.json();
        }
      } catch (error) {
        this.errorKey = error;
      }
      if (Object.keys(data).length !== 0) {
        const { error } = data;
        if (error) {
          this.errorKey = error;
        }
      }
    },
    moveItemFromLocalStorage(listData) {
      const indexItem = this.wishlistEntries.findIndex((x) => x.id === listData.item);
      this.itemRemovedMoved = {};
      this.wishlist.entries.splice(indexItem, 1);
    },
    async addToCart(listData) {
      const productData = listData || this.selectedItem;
      const addToCartPayload = {
        item: productData.id,
        sku: productData.sku,
        quantity: productData.quantity,
        price: productData.price,
        description: productData.description,
      };
      if (this.acceptanceTermsState) {
        addToCartPayload.termsCategoryAgreedTo = this.acceptanceTermsState;
      }
      try {
        const response = await ListsUpdateService.addToCart(addToCartPayload);
        if (response.ok) {
          this.getCartData(productData);
          this.cartEventGlobalNav();
          const indexItem = this.wishlistEntries.findIndex((x) => x.id === productData.id);
          this.wishlist.entries.splice(indexItem, 1);
        } else if (response.status === 412) {
          await response.json().then((data) => {
            this.addToCartState.cartData = { ...addToCartPayload, ...data };
            if (this.cartErrorData.scenario === 'terms') {
              this.handleTerms(productData.sku);
            }
            // Backorder scenarios
            if (this.cartErrorData.scenario === 'backorder') {
              this.handleBackorder(productData);
            }
          });
        } else if (response.status === 401 || response.status === 400 || response.status === 409) {
          response.json().then((data) => {
            const reason =
              data.error && data.error.length && data.error[0].message
                ? data.error[0].message
                : 'We have a connection problem. Please try again later.';
            this.openErrorModal(reason);
          });
        } else {
          this.openErrorModal('We have a connection problem. Please try again later.');
        }
        return response;
      } catch (error) {
        this.openErrorModal('We have a connection problem. Please try again later.');
        return null;
      }
    },
    async getCartData(productData) {
      let data;
      try {
        const response = await ListsUpdateService.getCart();
        data = response;
        if (response.ok) {
          data = await response.json();
        } else {
          data = {};
        }
      } catch (error) {
        this.openErrorModal('Error getting the cart data');
      }
      if (Object.keys(data).length === 0) {
        this.openErrorModal('Error getting the cart data');
      } else {
        this.cart = data;
        this.openModal({
          width: 'large',
          label: 'Added to cart',
          modalContent: 'AddToCartModal',
          modalProps: {
            productInfo: productData,
          },
          id: 'card-popup-modal',
        });
      }
    },
    cartEventGlobalNav() {
      const CustomEvent =
        window.CustomEvent ||
        function CustomEvent(name, opts) {
          const params = opts || { bubbles: false, cancelable: false, detail: null };
          const evt = document.createEvent('CustomEvent');
          evt.initCustomEvent(name, params.bubbles, params.cancelable, params.detail);
          return evt;
        };
      document.dispatchEvent(new CustomEvent('rei.gnav.cartCountChange'));
    },
    moveItemRemovedMoved(itemRemovedMoved) {
      this.itemRemovedMoved = itemRemovedMoved.itemRemovedMoved;
    },
    handleBackorder(product) {
      const url = this.addToCartState.backOrderURL
        .replace('{sku}', product.sku)
        .replace('{quantity}', product.quantity);
      this.redirect(`${url}&fromUrl=${window.location.pathname}`);
    },
    async handleTerms(sku) {
      await ListsUpdateService.getTermsAndConditions(sku).then((response) => {
        if (response.ok) {
          response.json().then((data) => {
            this.openModal({
              width: 'large',
              label: `Terms and Conditions: ${data.categoryTitle}`,
              modalContent: 'TermsAndConditionsModal',
              modalProps: {
                termsText: data.text,
              },
              id: 'terms-popup-modal',
            });
          });
        } else {
          this.openErrorModal('Missing terms and conditions content');
        }
      });
    },
    handleAcceptTerms() {
      this.closeModal();
      this.addToCartState.acceptTermsConditions = true;
      this.addToCart();
      this.addToCartState.acceptTermsConditions = null;
    },
    openErrorModal(message) {
      this.openModal({
        label: 'Error',
        labelIsTitle: true,
        modalContent: 'ErrorModal',
        modalProps: {
          message,
        },
        id: 'error-popup-modal',
      });
    },
    openModal(modalData) {
      this.addToCartState.modal = modalData;
      this.addToCartState.modalIsOpen = true;
    },
    closeModal() {
      this.addToCartState.modalIsOpen = false;
    },
    async createList(list) {
      let data;
      try {
        const response = await ListsCreateService.postList(list);
        data = response;
        data = await response.json();
      } catch (error) {
        this.serviceError = true;
      }
      const { id, error } = data;
      this.serviceError = false;

      if (!error) {
        this.redirect(`/lists/${id}`);
      } else {
        this.errorKey = error;
      }
    },
  },
});
